import React, { useEffect, useState } from 'react';
import Modal from '../../../../components/common/Modal';
import Button from '../../../../components/common/Button';
import { translate } from '../../../../utils/Common';
import styles from './Exceptions.module.css';

/**
 * Scope picker for the manual catalog re-push: the whole fleet (recovery broadcast) or specific
 * lockers (re-provisioning one or two lockers without a fleet-wide broadcast). Confirms with the
 * selected platform ids (null = fleet).
 */
const SyncScopeModal = ({ isOpen, onClose, onConfirm, isLoading, platforms, platformsLoading }) => {
  const [scope, setScope] = useState('all');
  const [selected, setSelected] = useState([]);

  // a fresh open always starts from the safe default (fleet, nothing pre-selected)
  useEffect(() => {
    if (isOpen) {
      setScope('all');
      setSelected([]);
    }
  }, [isOpen]);

  const toggleLocker = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleConfirm = () => {
    if (scope === 'specific' && !selected.length) return;
    onConfirm(scope === 'specific' ? selected : null);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={isLoading ? undefined : onClose}
      closeOnBackdropClick={!isLoading}
      showCloseButton={!isLoading}
      size="sm"
      title={translate('dashboard.configuration.exceptions.sync')}
    >
      <div className={styles.syncScope}>
        <p className={styles.formHelp}>{translate('dashboard.configuration.exceptions.syncScopeHelp')}</p>

        <label className={styles.syncScopeOption}>
          <input
            type="radio"
            name="syncScope"
            checked={scope === 'all'}
            onChange={() => setScope('all')}
            disabled={isLoading}
          />
          <span>{translate('dashboard.configuration.exceptions.syncScopeAll')}</span>
        </label>

        <label className={styles.syncScopeOption}>
          <input
            type="radio"
            name="syncScope"
            checked={scope === 'specific'}
            onChange={() => setScope('specific')}
            disabled={isLoading}
          />
          <span>{translate('dashboard.configuration.exceptions.syncScopeSpecific')}</span>
        </label>

        {scope === 'specific' && (
          <div className={styles.syncScopeList}>
            {platformsLoading && <small>{translate('global.loading') || 'Loading...'}</small>}
            {!platformsLoading && !platforms?.length && (
              <small>{translate('dashboard.configuration.exceptions.syncScopeNoLockers')}</small>
            )}
            {!platformsLoading &&
              platforms?.map((platform) => {
                const id = Number(platform.boxitLocationId);
                return (
                  <label key={id} className={styles.syncScopeItem}>
                    <input
                      type="checkbox"
                      checked={selected.includes(id)}
                      onChange={() => toggleLocker(id)}
                      disabled={isLoading}
                    />
                    <span>
                      {platform.locationName ?? platform.locationId ?? id}
                      <small> · {platform.locationId ?? id}</small>
                    </span>
                  </label>
                );
              })}
          </div>
        )}

        <div className={styles.formActions}>
          <Button type="button" variant="ghostOutline" onClick={onClose} disabled={isLoading}>
            {translate('global.cancel')}
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleConfirm}
            loading={isLoading}
            disabled={isLoading || (scope === 'specific' && !selected.length)}
          >
            {translate('dashboard.configuration.exceptions.sync')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default SyncScopeModal;
